import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EmailIcon from "@mui/icons-material/Email";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn"; // Architectural Request Icon

import moment from "moment";
import { motion } from "framer-motion"; // For animations
import api from "../../utils/api";

const ArchitecturalRequestTimeline = ({ architectural_request_id }) => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        const response = await api.get(
          `/architecturalRequestTimeline/${architectural_request_id}`
        );
        setTimelineData(response.data);
      } catch (error) {
        console.error("Error fetching timeline data:", error);
        setError("Failed to load timeline data.");
      } finally {
        setLoading(false);
      }
    };

    fetchTimeline();
  }, [architectural_request_id]);

  const combinedTimeline = useMemo(() => {
    if (!timelineData || !timelineData.architecturalRequest) return [];

    const { architecturalRequest, comments = [], events = [], emails = [] } = timelineData;

    const timeline = [
      { type: "architecturalRequest", title: "Architectural Request Submitted", time: architecturalRequest.created_at },
      ...comments.map(({ comment_text, sent_at }) => ({
        type: "comment",
        title: comment_text,
        time: sent_at || new Date().toISOString(),
      })),
      ...events.map(({ title, created_at }) => ({
        type: "event",
        title,
        time: created_at || new Date().toISOString(),
      })),
      ...emails.map(({ subject, sent_at }) => ({
        type: "email",
        title: subject,
        time: sent_at || new Date().toISOString(),
      })),
      ...(architecturalRequest.reviewed_at
        ? [{ type: "architecturalRequest", title: "Request Reviewed", time: architecturalRequest.reviewed_at }]
        : []),
    ];

    return timeline.sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [timelineData]);

  if (loading) return <Typography>Loading timeline...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: "bold", color: "#333" }}>
        Architectural Request Timeline
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          paddingLeft: 3,
          borderLeft: "2px solid #ccc",
        }}
      >
        {combinedTimeline.map(({ type, title, time }, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card
              sx={{
                position: "relative",
                mb: 2,
                padding: 1.5,
                boxShadow: 1,
                borderRadius: 2,
                maxWidth: "80%",
                bgcolor: "#fff",
                border: "1px solid #ddd",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: 3,
                  transform: "scale(1.02)",
                },
              }}
            >
              {/* Timeline Circle */}
              <CircleIcon
                sx={{
                  color: {
                    architecturalRequest: "#1E88E5",
                    comment: "#43A047",
                    event: "#8E24AA",
                    email: "#FB8C00",
                  }[type] || "#757575",
                  fontSize: 14,
                  position: "absolute",
                  left: "-25px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />

              {/* Content */}
              <CardContent sx={{ padding: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  {type === "architecturalRequest" && <AssignmentTurnedInIcon sx={{ color: "#1E88E5", mr: 1, fontSize: 20 }} />}
                  {type === "comment" && <ChatBubbleIcon sx={{ color: "#43A047", mr: 1, fontSize: 20 }} />}
                  {type === "event" && <EventNoteIcon sx={{ color: "#8E24AA", mr: 1, fontSize: 20 }} />}
                  {type === "email" && <EmailIcon sx={{ color: "#FB8C00", mr: 1, fontSize: 20 }} />}
                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "#333" }}>
                    {title}
                  </Typography>
                </Box>

                <Typography
  variant="caption"
  sx={{
    fontSize: "0.75rem",
    color: "text.secondary",
    fontStyle: "italic",
    display: "block",
    mt: 0.5,
  }}
>
  🕒 Added on {moment.parseZone(time).format("YYYY-MM-DD [at] HH:mm")}
</Typography>


              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>

      {/* Legend */}
      <Legend />
    </Box>
  );
};

// **Legend Component**
const Legend = () => (
  <Box sx={{ mt: 3 }}>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {[
        { color: "#1E88E5", icon: <AssignmentTurnedInIcon sx={{ fontSize: 14, mr: 1, color: "#1E88E5" }} />, label: "Request Submitted/Reviewed" },
        { color: "#43A047", icon: <ChatBubbleIcon sx={{ fontSize: 14, mr: 1, color: "#43A047" }} />, label: "Comments" },
        { color: "#8E24AA", icon: <EventNoteIcon sx={{ fontSize: 14, mr: 1, color: "#8E24AA" }} />, label: "Events" },
        { color: "#FB8C00", icon: <EmailIcon sx={{ fontSize: 14, mr: 1, color: "#FB8C00" }} />, label: "Email Logs" },
      ].map(({ color, icon, label }, index) => (
        <Box key={index} sx={{ display: "flex", alignItems: "center", mr: 3 }}>
          {icon}
          <Typography variant="body2" sx={{ color }}>
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

export default ArchitecturalRequestTimeline;
